import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { ExecutionMetricsChartPanel } from '@/features/executions/components/ExecutionMetricsChartPanel';
import { querySuiteExecutions } from '@/features/executions/data';
import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { DetailCloneTablePanel } from '@/features/shared/components/cloneTable/DetailCloneTablePanel';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import {
  useCloneDateWindow,
  type CloneDateQuickFilter,
} from '@/features/shared/hooks/useCloneDateWindow';
import { resolveEntityTags } from '@/features/shared/tags';
import { SuiteDetailsPanels } from '@/features/suites/components/SuiteDetailsPanels';
import { SuiteExecutionsTableClone } from '@/features/suites/components/SuiteExecutionsTableClone';
import { SuiteExecutionsCloneToolbar } from '@/features/suites/pages/SuiteExecutionsCloneToolbar';
import {
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  cloneExecutionSortFieldMap,
  type CloneExecutionSortDirection,
  type CloneExecutionSortKey,
} from '@/features/suites/pages/suiteExecutionsPageCloneConfig';
import { TestsTableClone } from '@/features/tests/components/TestsTableClone';
import { queryTests } from '@/features/tests/data/tests';
import { CloneToolbar } from '@/features/tests/pages/TestsCloneToolbar';
import {
  cloneDefaultColumnVisibilityModel,
  cloneDefaultSortDirection,
  cloneDefaultSortKey,
  cloneSortFieldMap,
  type CloneSortDirection,
  type CloneSortKey,
} from '@/features/tests/pages/testsPageCloneConfig';
import { s } from '@/theme/tokens';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Stack } from '@/ui/layout/Stack';
import { Box, type GridColumnVisibilityModel } from '@/ui/primitives';
import { Text } from '@/ui/primitives/Text';
import type { SuiteDetailPageViewModel } from '@/features/suites/hooks/useSuiteDetailPageViewModel';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteDetailDto,
  SuiteExecutionDto,
  SuiteExecutionStatus,
  TestExecutionStatus,
  TestListItemDto,
} from '@flowqa/shared';

type SuiteDetailContentProps = {
  suite: SuiteDetailDto;
  viewModel: SuiteDetailPageViewModel;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const SuiteDetailContent = ({
  suite,
  viewModel,
  onDelete,
  deleteLoading = false,
}: SuiteDetailContentProps): ReactElement => {
  const hasCustomDates = Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay);
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneExecutionDefaultColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    SuiteExecutionStatus[]
  >([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [cloneVisibleExecutionRows, setCloneVisibleExecutionRows] = useState<SuiteExecutionDto[]>(
    [],
  );
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneDefaultColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(cloneDefaultSortKey);
  const [cloneSortDirection, setCloneSortDirection] =
    useState<CloneSortDirection>(cloneDefaultSortDirection);
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<TestExecutionStatus[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneVisibleRows, setCloneVisibleRows] = useState<TestListItemDto[]>([]);

  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneExecutionAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleExecutionRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleExecutionRows]);
  const cloneAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleRows]);
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suite executions found for selected date range'
    : formatRangeWindowNoDataText('No suite executions found', cloneDateQuickFilter, false);
  const cloneTestsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No tests found for selected date range'
    : formatRangeWindowNoDataText('No tests found', cloneDateQuickFilter, false);
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  const cloneExecutionTableInstanceKey = useMemo(
    () =>
      [
        viewModel.suiteId,
        cloneDateQuickFilter,
        cloneDateWindow.startDay ?? 'none',
        cloneDateWindow.endDay ?? 'none',
        cloneExecutionStatusFilterKey,
        cloneExecutionTagFilterKey,
        debouncedCloneExecutionSearch,
      ].join(':'),
    [
      cloneDateQuickFilter,
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneExecutionStatusFilterKey,
      cloneExecutionTagFilterKey,
      debouncedCloneExecutionSearch,
      viewModel.suiteId,
    ],
  );
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );
  const cloneExecutionFixedFilters = useMemo(() => {
    const isLatestQuickFilter =
      cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
    return mergeFilters(
      buildEqFilterValue('suiteId', viewModel.suiteId),
      buildInFilter('status', cloneExecutionFilterStatuses),
      buildInFilter('tags', cloneExecutionFilterTags),
      isLatestQuickFilter
        ? buildEqFilterValue('isLatest', true)
        : buildDateRangeFilters(
            'timestamp',
            cloneDateWindow.startDay
              ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
              : null,
            cloneDateWindow.endDay
              ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
              : null,
          ),
    );
  }, [
    cloneDateQuickFilter,
    cloneDateWindow.endDay,
    cloneDateWindow.startDay,
    cloneExecutionFilterStatuses,
    cloneExecutionFilterTags,
    cloneFromDate,
    cloneToDate,
    viewModel.suiteId,
  ]);
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteExecutionDto>> => {
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return querySuiteExecutions(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        search: debouncedCloneExecutionSearch,
        sorts: [sortOverride],
      });
    },
    [
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      debouncedCloneExecutionSearch,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );
  const cloneTestsQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const mappedFilters = input.filters.map((filter) =>
        filter.field === 'statusFilter'
          ? {
              ...filter,
              field: 'latestExecutionStatus',
            }
          : filter,
      );
      const toolbarFilters = mergeFilters(
        buildEqFilterValue('suiteId', viewModel.suiteId),
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: cloneSortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        search: debouncedCloneSearch,
        sorts: [sortOverride],
        filters: [...mappedFilters, ...toolbarFilters],
        metricsStartDay: cloneDateWindow.startDay ?? undefined,
        metricsEndDay: cloneDateWindow.endDay ?? undefined,
        previousMetricsStartDay: cloneDateWindow.previousStartDay ?? undefined,
        previousMetricsEndDay: cloneDateWindow.previousEndDay ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestExecution: true,
          metrics: true,
          metricDeltas: viewModel.showTableDeltas,
        },
      });
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      debouncedCloneSearch,
      selectedOrganisationId,
      selectedProjectId,
      viewModel.showTableDeltas,
      viewModel.suiteId,
    ],
  );
  const suiteExecutionsNoDataText = formatRangeWindowNoDataText(
    'No suite executions found',
    viewModel.range,
    hasCustomDates,
  );

  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Suite" />
          {viewModel.title}
        </Text>
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Suite"
          dialogDescription={
            <Stack gap="sm">
              <Text role="body" color="textSecondary">
                Deleting this suite also removes associated tests, suite executions, and test
                executions.
              </Text>
              <Text role="body" color="textSecondary">
                This action cannot be undone.
              </Text>
            </Stack>
          }
          confirmLabel="Delete Suite"
        />
      }
      metadata={<SuiteDetailsPanels suite={suite} />}
      primaryContent={
        <Stack gap="none">
          <Box sx={{ mt: s('md') }}>
            <Stack gap="md">
              <Box
                sx={{
                  borderTop: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  py: s('lg'),
                }}
              >
                <PageHeader
                  title="Suite Executions"
                  actions={
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      gap="md"
                      sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
                    >
                      <TagsFilterField
                        value={viewModel.selectedTags}
                        options={viewModel.tagOptions}
                        onChange={viewModel.handleTagsChange}
                        emptyLabel={viewModel.tagOptionsLoading ? 'Loading Tags...' : ''}
                      />
                      <DashboardDateRangeControls
                        value={viewModel.range}
                        onChange={(nextRange) => {
                          viewModel.setRange(nextRange);
                          viewModel.setSelectedStartDay(null);
                          viewModel.setSelectedEndDay(null);
                        }}
                        displayStartDay={viewModel.pickerStartDay}
                        displayEndDay={viewModel.pickerEndDay}
                        isCustomRange={
                          Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay) &&
                          !viewModel.isAllMode
                        }
                        onSelectedStartDayChange={viewModel.handleStartDayChange}
                        onSelectedEndDayChange={viewModel.handleEndDayChange}
                        fullWidthOnMobile
                      />
                    </Stack>
                  }
                />
              </Box>
              <Box sx={{ mt: s('md') }}>
                <Stack gap="lg">
                  <StatusMetricCardsRow cards={viewModel.suiteMetricCards} />
                  <MetricValueCardsRow items={viewModel.metricValueItems} />
                </Stack>
              </Box>
              <Box sx={{ mt: s('md') }}>
                <ExecutionMetricsChartPanel
                  chartTab={viewModel.chartTab}
                  tabsAriaLabel="Suite detail chart tabs"
                  onChartTabChange={viewModel.setChartTab}
                  showChartDateClear={viewModel.showChartDateClear}
                  onChartDateClear={viewModel.handleChartDateClear}
                  chartHeight={viewModel.chartHeight}
                  suppressChartAnimation={viewModel.suppressChartAnimation}
                  stackedChartData={viewModel.stackedChartData}
                  passRateChartData={viewModel.passRateChartData}
                  frequencyChartData={viewModel.frequencyChartData}
                  durationChartData={viewModel.durationChartData}
                  passRateGuideLines={viewModel.passRateGuideLines}
                  frequencyGuideLines={viewModel.frequencyGuideLines}
                  durationGuideLines={viewModel.durationGuideLines}
                  onChartSelect={viewModel.handleChartSelect}
                  showLineChartPoints={viewModel.showLineChartPoints}
                  emptyStateTitle={suiteExecutionsNoDataText}
                />
              </Box>
              <Box sx={{ mt: s('lg') }}>
                <DetailCloneTablePanel
                  tabLabel="Executions"
                  tabsAriaLabel="Suite detail executions panel"
                  toolbarSlot={
                    <SuiteExecutionsCloneToolbar
                      title={undefined}
                      onDebouncedCloneSearchChange={setDebouncedCloneExecutionSearch}
                      columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                      onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                      sortKey={cloneExecutionSortKey}
                      sortDirection={cloneExecutionSortDirection}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setCloneExecutionSortKey(nextSortKey);
                        setCloneExecutionSortDirection(nextSortDirection);
                      }}
                      filterStatuses={cloneExecutionFilterStatuses}
                      onFilterStatusesChange={setCloneExecutionFilterStatuses}
                      filterTags={cloneExecutionFilterTags}
                      onFilterTagsChange={setCloneExecutionFilterTags}
                      dateQuickFilter={cloneDateQuickFilter}
                      onDateQuickFilterChange={(next) => {
                        setCloneDateQuickFilter(next);
                        setCloneFromDate(null);
                        setCloneToDate(null);
                      }}
                      fromDate={cloneFromDate}
                      toDate={cloneToDate}
                      onFromDateChange={setCloneFromDate}
                      onToDateChange={setCloneToDate}
                      availableTags={cloneExecutionAvailableTags}
                    />
                  }
                >
                  <SuiteExecutionsTableClone
                    key={cloneExecutionTableInstanceKey}
                    persistKey={null}
                    runColumnFirst
                    queryKey={[
                      'suite-detail-executions-table-clone',
                      viewModel.suiteId,
                      selectedOrganisationId ?? null,
                      selectedProjectId ?? null,
                      cloneDateQuickFilter,
                      cloneExecutionSortKey,
                      cloneExecutionSortDirection,
                      cloneExecutionStatusFilterKey,
                      cloneExecutionTagFilterKey,
                      debouncedCloneExecutionSearch,
                      cloneDateWindow.startDay,
                      cloneDateWindow.endDay,
                    ]}
                    queryFn={cloneExecutionQueryFn}
                    fixedFilters={cloneExecutionFixedFilters}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialPageSize={10}
                    noRowsText={cloneExecutionsNoRowsText}
                    showToolbar={false}
                    disableColumnSorting
                    disableColumnMenu
                    search={debouncedCloneExecutionSearch}
                    onRowsChange={setCloneVisibleExecutionRows}
                    columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                    onColumnVisibilityModelChange={(nextModel) => {
                      setCloneExecutionColumnVisibilityModel({
                        ...nextModel,
                        name: true,
                      });
                    }}
                  />
                </DetailCloneTablePanel>
              </Box>
              <Box sx={{ mt: s('lg') }}>
                <DetailCloneTablePanel
                  tabLabel="Tests"
                  tabsAriaLabel="Suite detail tests panel"
                  toolbarSlot={
                    <CloneToolbar
                      title={undefined}
                      onDebouncedCloneSearchChange={setDebouncedCloneSearch}
                      columnVisibilityModel={cloneColumnVisibilityModel}
                      onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
                      sortKey={cloneSortKey}
                      sortDirection={cloneSortDirection}
                      onSortChange={(nextSortKey, nextSortDirection) => {
                        setCloneSortKey(nextSortKey);
                        setCloneSortDirection(nextSortDirection);
                      }}
                      filterStatuses={cloneFilterStatuses}
                      onFilterStatusesChange={setCloneFilterStatuses}
                      filterTags={cloneFilterTags}
                      onFilterTagsChange={setCloneFilterTags}
                      dateQuickFilter={cloneDateQuickFilter}
                      onDateQuickFilterChange={(next) => {
                        setCloneDateQuickFilter(next);
                        setCloneFromDate(null);
                        setCloneToDate(null);
                      }}
                      fromDate={cloneFromDate}
                      toDate={cloneToDate}
                      onFromDateChange={setCloneFromDate}
                      onToDateChange={setCloneToDate}
                      availableTags={cloneAvailableTags}
                    />
                  }
                >
                  <TestsTableClone
                    persistKey={null}
                    queryKey={[
                      'suite-detail-tests-table-clone',
                      viewModel.suiteId,
                      selectedOrganisationId ?? null,
                      selectedProjectId ?? null,
                      cloneDateQuickFilter,
                      cloneSortKey,
                      cloneSortDirection,
                      cloneStatusFilterKey,
                      cloneTagFilterKey,
                      debouncedCloneSearch,
                      cloneDateWindow.startDay,
                      cloneDateWindow.endDay,
                    ]}
                    queryFn={cloneTestsQueryFn}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialPageSize={10}
                    showDeltas={viewModel.showTableDeltas}
                    sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
                    metricsStartDay={cloneDateWindow.startDay ?? undefined}
                    metricsEndDay={cloneDateWindow.endDay ?? undefined}
                    noRowsText={cloneTestsNoRowsText}
                    showToolbar={false}
                    disableColumnSorting
                    disableColumnMenu
                    showSuiteLinks={false}
                    includeSuiteInNameColumn={false}
                    search={debouncedCloneSearch}
                    onRowsChange={setCloneVisibleRows}
                    columnVisibilityModel={cloneColumnVisibilityModel}
                    onColumnVisibilityModelChange={(nextModel) => {
                      setCloneColumnVisibilityModel({
                        ...nextModel,
                        name: true,
                      });
                    }}
                  />
                </DetailCloneTablePanel>
              </Box>
            </Stack>
          </Box>
        </Stack>
      }
    />
  );
};
